// Invite activation (plan 8.4.1 "계정이 생기는 길" · 7.1.4, BUILD-SPEC-api auth.invite.lookup/accept): the code an admin
// sent (address /login?invite=AIAX-…-…, or typed here) → GET /auth/invite says who it is for and until when → the
// person picks a user ID, name and password (same rules as every password form) → POST /auth/invite makes the account
// → back to the sign-in form with the user ID filled in. The first sign-in then goes through the gates as usual.
import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../../api/client';
import { useFmt, useT } from '../../i18n';
import { cls } from '../../lib/util';
import { Icon } from '../../ui/Icon';
import { Alert } from '../../ui/States';
import { toast } from '../../ui/layers';
import { LOGIN_ID, acceptInvite, lookupInvite, normalizeInviteCode, type InviteLookup } from './api';
import { AuthCard, Brand, CardTitle, PasswordRules, PwField, Spin, TextField, isCode, pwRulesOk } from './parts';

type Field = 'code' | 'loginId' | 'displayName' | 'p1' | 'p2';
const FIELD_ID: Record<Field, string> = { code: '#lg-iv-code', loginId: '#lg-iv-id', displayName: '#lg-iv-name', p1: '#lg-gp1', p2: '#lg-gp2' };
const focus = (f: Field) => requestAnimationFrame(() => document.querySelector<HTMLElement>(FIELD_ID[f])?.focus());

export function InviteCard({ initialCode, onBack, onCreated }: { initialCode: string; onBack: () => void; onCreated: (loginId: string) => void }) {
  const t = useT();
  const fmt = useFmt();
  const [code, setCode] = useState(initialCode);
  const [found, setFound] = useState<{ code: string; info: InviteLookup } | null>(null);
  const [busy, setBusy] = useState(false);
  const [codeErr, setCodeErr] = useState<string | null>(null);
  const [f, setF] = useState({ loginId: '', displayName: '', p1: '', p2: '' });
  const [err, setErr] = useState<{ msg: string; field: Field | null } | null>(null);

  async function check(raw: string) {
    const c = normalizeInviteCode(raw);
    setCode(c);
    setErr(null);
    if (c.length < 8) {
      setCodeErr(t('login.invite.err.code'));
      focus('code');
      return;
    }
    setCodeErr(null);
    setBusy(true);
    try {
      const info = await lookupInvite(c);
      if (!info.valid) {
        setCodeErr(t('login.invite.invalid'));
        focus('code');
      } else {
        setFound({ code: c, info });
        focus('loginId');
      }
    } catch (e) {
      if (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 429) {
        setCodeErr(t('login.invite.invalid'));
        focus('code');
      } else setErr({ msg: t('login.err.server'), field: null });
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (initialCode) void check(initialCode);
    // a code that came with the address is checked once on arrival
  }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (busy || !found) return;
    const empty = (['loginId', 'displayName', 'p1', 'p2'] as const).find((k) => !f[k].trim());
    if (empty) {
      setErr({ msg: t('login.setup.err.required'), field: empty });
      focus(empty);
      return;
    }
    if (!LOGIN_ID.test(f.loginId.trim())) {
      setErr({ msg: t('login.invite.err.loginId'), field: 'loginId' });
      focus('loginId');
      return;
    }
    if (!pwRulesOk(f.p1, f.p2, f.loginId)) {
      setErr({ msg: t('login.gate.pw.err'), field: 'p1' });
      focus('p1');
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await acceptInvite({ code: found.code, loginId: f.loginId.trim(), password: f.p1, displayName: f.displayName.trim() });
      toast(t('login.invite.done'), 'ok');
      onCreated(f.loginId.trim());
    } catch (e2) {
      if (isCode(e2, 'name_duplicate') || isCode(e2, 'conflict')) {
        setErr({ msg: t('login.invite.err.taken'), field: 'loginId' });
        focus('loginId');
      } else if (isCode(e2, 'password_policy')) {
        setErr({ msg: t('login.gate.pw.errServer'), field: 'p1' });
        focus('p1');
      } else if (e2 instanceof ApiError && (e2.status === 404 || e2.status === 410 || isCode(e2, 'token_invalid') || isCode(e2, 'setup_code_invalid'))) {
        // the code expired or was used in the meantime: start again from the code
        setFound(null);
        setCodeErr(t('login.invite.invalid'));
        focus('code');
      } else if (e2 instanceof ApiError && e2.status >= 400 && e2.status < 500) setErr({ msg: t('login.invite.err.server'), field: null });
      else setErr({ msg: t('login.err.server'), field: null });
    } finally {
      setBusy(false);
    }
  }

  const upd = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  const described = 'lg-gp-rules' + (err?.field ? ' lg-iv-err' : '');
  return (
    <AuthCard variant="lg-card--gate">
      <Brand />
      <p className="lg-kicker">
        <Icon name="key" />
        {t('login.invite.kicker')}
      </p>
      <CardTitle>{t('login.invite.title')}</CardTitle>
      <p className="auth-card__sub">{t(found ? 'login.invite.descOk' : 'login.invite.desc')}</p>
      {err && !err.field ? (
        <div className="lg-alerts">
          <Alert tone="danger" title={err.msg} />
        </div>
      ) : null}
      {!found ? (
        <form
          className="form lg-form"
          noValidate
          aria-busy={busy || undefined}
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy) void check(code);
          }}
        >
          <div className="field">
            <label className="field__label" htmlFor="lg-iv-code">
              {t('login.invite.code')}
            </label>
            <input
              className="input lg-input lg-invite-code"
              id="lg-iv-code"
              type="text"
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder="AIAX-XXXX-XXXX"
              value={code}
              aria-invalid={codeErr ? true : undefined}
              aria-describedby={'lg-iv-code-hint' + (codeErr ? ' lg-iv-code-err' : '')}
              onChange={(e) => {
                setCode(e.target.value);
                if (codeErr) setCodeErr(null);
              }}
            />
            <p className="field__hint" id="lg-iv-code-hint">
              {t('login.invite.codeHint')}
            </p>
            {codeErr ? (
              <p className="field__error" id="lg-iv-code-err" role="alert">
                {codeErr}
              </p>
            ) : null}
          </div>
          <button className={cls('btn btn--primary btn--lg btn--block', busy && 'is-busy')} type="submit" aria-disabled={busy || undefined}>
            {busy ? <Spin /> : null}
            <span>{t(busy ? 'login.invite.checking' : 'login.invite.check')}</span>
          </button>
        </form>
      ) : (
        <form className="form lg-form" noValidate aria-busy={busy || undefined} onSubmit={create}>
          <div className="lg-invite-ok" role="status">
            <p className="lg-invite-ok__title">
              <Icon name="check" />
              {t('login.invite.okTitle')}
            </p>
            <dl className="facts">
              {found.info.emailMasked ? (
                <>
                  <dt>{t('login.invite.email')}</dt>
                  <dd>{found.info.emailMasked}</dd>
                </>
              ) : null}
              {found.info.team ? (
                <>
                  <dt>{t('login.invite.team')}</dt>
                  <dd>{found.info.team}</dd>
                </>
              ) : null}
              {found.info.expiresAt ? (
                <>
                  <dt>{t('login.invite.expires')}</dt>
                  <dd>{t('login.invite.until', { at: fmt.full(found.info.expiresAt) })}</dd>
                </>
              ) : null}
            </dl>
          </div>
          <TextField id="lg-iv-id" label={t('login.id')} value={f.loginId} onChange={upd('loginId')} autoComplete="username" hint={t('login.invite.loginIdHint')} error={err?.field === 'loginId'} />
          <TextField id="lg-iv-name" label={t('login.setup.displayName')} value={f.displayName} onChange={upd('displayName')} autoComplete="name" error={err?.field === 'displayName'} />
          <PwField id="lg-gp1" label={t('login.gate.pw.new')} value={f.p1} onChange={upd('p1')} invalid={err?.field === 'p1'} describedBy={described} />
          <PwField id="lg-gp2" label={t('login.gate.pw.confirm')} value={f.p2} onChange={upd('p2')} invalid={err?.field === 'p2'} describedBy={described} />
          <PasswordRules id="lg-gp-rules" p1={f.p1} p2={f.p2} loginId={f.loginId} />
          {err?.field ? (
            <p className="field__error" id="lg-iv-err" role="alert">
              {err.msg}
            </p>
          ) : null}
          <button className={cls('btn btn--primary btn--lg btn--block', busy && 'is-busy')} type="submit" aria-disabled={busy || undefined}>
            {busy ? <Spin /> : null}
            <span>{t(busy ? 'login.invite.busy' : 'login.invite.submit')}</span>
          </button>
          <button
            className="btn btn--ghost btn--block"
            type="button"
            onClick={() => {
              setFound(null);
              setErr(null);
              focus('code');
            }}
          >
            {t('login.invite.otherCode')}
          </button>
        </form>
      )}
      <button className="btn btn--ghost btn--block lg-back" type="button" onClick={onBack}>
        <Icon name="chevL" />
        {t('login.invite.back')}
      </button>
    </AuthCard>
  );
}
